import { useFormik } from 'formik';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiMail,
  FiMapPin,
  FiPhone,
  FiTag,
  FiUser,
  FiX
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

// Type-cast Feather icons for React compatibility

const FiCheckComponent = FiCheck as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiUserComponent = FiUser as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiMailComponent = FiMail as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiTagComponent = FiTag as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiCalendarComponent = FiCalendar as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
// const FiBarChart2Component = FiBarChart2 as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;
const FiMapPinComponent = FiMapPin as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiPhoneComponent = FiPhone as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Interface for preview data
interface PreviewData {
  headers: string[];
  rows: any[];
  fileName: string;
}

// Props for the ColumnMapping component
interface ColumnMappingProps {
  previewData: PreviewData;
  mappings: { [key: string]: string };
  setMappings: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  customFieldNames: { [key: string]: string };
  setCustomFieldNames: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >;
  possibleTargets: string[];
}

// Ref interface for validation
export interface ColumnMappingRef {
  validate: () => Promise<boolean>;
}

// Validation schema for custom field names
const validationSchema = Yup.object().shape({
  customFieldNames: Yup.object().test(
    'custom-field-validation',
    'Custom field validation',
    function (value: { [key: string]: string } = {}) {
      const { mappings } = this.parent;

      for (const [header, mapping] of Object.entries(mappings)) {
        if (mapping === 'add_new') {
          const customFieldName = value?.[header] || '';
          if (!customFieldName.trim()) {
            return this.createError({
              path: `customFieldNames.${header}`,
              message: 'This field cannot be empty',
            });
          }
        }
      }
      return true;
    }
  ),
});

// Returns appropriate icon for field type
const getFieldIcon = (fieldName: string) => {
  if (!fieldName) return null;

  const field = fieldName.toLowerCase();

  if (field.includes('email') || field.includes('mail')) {
    return <FiMailComponent className='text-gray-500' size={16} />;
  } else if (field.includes('first') || field.includes('last') || field.includes('name')) {
    return <FiUserComponent className='text-gray-500' size={16} />;
  } else if (field.includes('phone') || field.includes('contact')) {
    return <FiPhoneComponent className='text-gray-500' size={16} />;
  } else if (field.includes('dob') || field.includes('date')) {
    return <FiCalendarComponent className='text-gray-500' size={16} />;
  } else if (field.includes('gender')) {
    return <FiUserComponent className='text-gray-500' size={16} />;
  } else if (field.includes('status')) {
    return <FiCheckComponent className='text-gray-500' size={16} />;
  } else if (field.includes('tag')) {
    return <FiTagComponent className='text-gray-500' size={16} />;
  } else if (field.includes('segment')) {
    return <FiTagComponent className='text-gray-500' size={16} />;
  } else if (field.includes('address') || field.includes('city') || field.includes('country')) {
    return <FiMapPinComponent className='text-gray-500' size={16} />;
  }

  return <FiUserComponent className='text-gray-500' size={16} />;
};

// Component for mapping file columns to audience fields
const ColumnMapping = forwardRef<ColumnMappingRef, ColumnMappingProps>(
  (
    {
      previewData,
      mappings,
      setMappings,
      customFieldNames,
      setCustomFieldNames,
      possibleTargets,
    },
    ref
  ) => {
    // Formik for form state and validation
    const formik = useFormik({
      initialValues: {
        mappings,
        customFieldNames,
      },
      validationSchema,
      enableReinitialize: true,
      validateOnMount: false,
      validateOnChange: true,
      validateOnBlur: true,
      onSubmit: () => { },
    });

    // State for tracking active 'add new' fields
    const [isAddNewActive, setIsAddNewActive] = useState<{
      [key: string]: boolean;
    }>({});

    // Sync formik mappings with parent state
    React.useEffect(() => {
      setMappings(formik.values.mappings);
    }, [formik.values.mappings, setMappings]);

    // Sync formik custom field names with parent state
    React.useEffect(() => {
      setCustomFieldNames(formik.values.customFieldNames);
    }, [formik.values.customFieldNames, setCustomFieldNames]);

    // Expose validate method via ref
    useImperativeHandle(ref, () => ({
      validate: async (): Promise<boolean> => {
        const errors = await formik.validateForm();
        formik.setTouched(
          {
            customFieldNames: Object.keys(customFieldNames).reduce(
              (acc, key) => {
                if (mappings[key] === 'add_new') {
                  acc[key] = true;
                }
                return acc;
              },
              {} as any
            ),
          },
          false
        );

        const mappedValues = new Set<string>();
        // const customFieldNamesArray = Object.values(customFieldNames)
        //   .filter((name) => name && name.trim() !== '')
        //   .map((name) => name.toLowerCase().trim());

        for (const [header, mapping] of Object.entries(mappings)) {
          if (mapping && mapping !== 'add_new') {
            const normalizedMapping = mapping.toLowerCase().trim();
            if (mappedValues.has(normalizedMapping)) {
              toast.error('Cannot add duplicate fields');
              formik.setFieldError(
                `mappings.${header}`,
                'This column is already mapped'
              );
              return false;
            }
            mappedValues.add(normalizedMapping);
          } else if (mapping === 'add_new') {
            const customFieldName = customFieldNames[header] || '';
            if (customFieldName.trim()) {
              const normalizedCustomName = customFieldName.toLowerCase().trim();
              if (
                possibleTargets
                  .map((target) => target.toLowerCase().trim())
                  .includes(normalizedCustomName) ||
                mappedValues.has(normalizedCustomName)
              ) {
                toast.error('Cannot add duplicate fields');
                formik.setFieldError(
                  `customFieldNames.${header}`,
                  'Field name already exists'
                );
                return false;
              }
              mappedValues.add(normalizedCustomName);
            }
          }
        }

        return Object.keys(errors).length === 0;
      },
    }));

    // Memoized currently mapped values
    const currentlyMappedValues = React.useMemo(() => {
      return Object.values(mappings).filter(
        (value) => value && value !== 'add_new'
      );
    }, [mappings]);

    // Returns available target fields for mapping
    const getAvailableTargets = (currentHeader: string) => {
      return possibleTargets.filter((target) => {
        const isAlreadyMapped = currentlyMappedValues.includes(target);
        const isCurrentMapping = mappings[currentHeader] === target;
        return !isAlreadyMapped || isCurrentMapping;
      });
    };

    // Removes a new field mapping
    const handleRemoveNewField = (header: string) => {
      formik.setFieldValue(`mappings.${header}`, '');
      formik.setFieldValue(`customFieldNames.${header}`, '');
      setIsAddNewActive((prev) => ({ ...prev, [header]: false }));
    };

    // Renders the column mapping interface
    return (
      <div className='mt-4'>
        <div className='flex justify-between mb-4'>
          <div className='font-medium text-[#1F2937] text-[18px] w-1/2'>
            CSV columns
          </div>
          <div className='font-medium text-[#1F2937] text-[18px] w-1/2 flex items-center'>
            Existing system columns
          </div>
        </div>
        <div className='max-h-[calc(70vh-100px)] overflow-y-auto'>
          {previewData.headers.map((header) => {
            const availableTargets = getAvailableTargets(header);
            const hasMappingError =
              formik.errors.mappings?.[header] &&
              formik.touched.mappings?.[header];
            const hasCustomFieldError =
              mappings[header] === 'add_new' &&
              formik.errors.customFieldNames?.[header] &&
              formik.touched.customFieldNames?.[header];

            const selectedValue =
              mappings[header] && mappings[header] !== 'add_new'
                ? mappings[header]
                : '';
            const selectedFieldIcon = getFieldIcon(selectedValue);
            const isChooseColumn = selectedValue === '';
            const isAddNewField = mappings[header] === 'add_new';

            return (
              <div
                key={header}
                className='flex items-center justify-between mb-4 border-b pb-4'
              >
                <div className='w-1/2'>
                  <div className='font-medium text-gray-900'>{header}</div>
                  {/* <div className='text-sm text-gray-500'>
                    {previewData.rows[0]?.[header]?.toString() || ''}
                  </div> */}
                </div>
                <div className='w-1/2 flex items-center'>
                  <div className='w-full'>
                    <div className='relative'>
                      <select
                        name={`mappings.${header}`}
                        value={selectedValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          formik.setFieldValue(`mappings.${header}`, value);
                          if (value === 'add_new') {
                            setIsAddNewActive((prev) => ({
                              ...prev,
                              [header]: true,
                            }));
                            formik.setFieldValue(
                              `customFieldNames.${header}`,
                              ''
                            );
                          } else {
                            setIsAddNewActive((prev) => ({
                              ...prev,
                              [header]: false,
                            }));
                            formik.setFieldValue(
                              `customFieldNames.${header}`,
                              ''
                            );
                          }
                        }}
                        onBlur={formik.handleBlur}
                        className={`w-full pr-10 py-2 border rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none ${hasMappingError ? 'border-red-500' : 'border-gray-300'
                          } ${isChooseColumn
                            ? 'pl-3 text-gray-400'
                            : 'pl-10 text-gray-700'
                          }`}
                      >
                        <option value='' className='text-gray-600'>
                          Choose column
                        </option>
                        {availableTargets.map((field) => (
                          <option
                            key={field}
                            value={field}
                            className='text-gray-700'
                          >
                            {field}
                          </option>
                        ))}
                        <option value='add_new' className='text-gray-700'>
                          + Add new field
                        </option>
                      </select>

                      {!isChooseColumn && (
                        <div className='absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none'>
                          {selectedFieldIcon}
                        </div>
                      )}

                      <FiChevronDownComponent
                        className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none'
                        size={16}
                      />
                    </div>
                    {hasMappingError && (
                      <div className='text-red-500 text-sm mt-1'>
                        {formik.errors.mappings?.[header]}
                      </div>
                    )}
                    {hasCustomFieldError && (
                      <div className='text-transparent text-sm mt-1'>text</div>
                    )}
                  </div>

                  {isAddNewField && isAddNewActive[header] && (
                    <div className='ml-4 w-full'>
                      <div className='relative'>
                        <input
                          type='text'
                          name={`customFieldNames.${header}`}
                          placeholder='Enter new field name'
                          className={`w-full pl-3 pr-8 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${hasCustomFieldError
                            ? 'border-red-500'
                            : 'border-gray-300'
                            }`}
                          value={customFieldNames[header] || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        <button
                          onClick={() => handleRemoveNewField(header)}
                          className='absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500'
                        >
                          <FiXComponent size={16} />
                        </button>
                      </div>
                      {hasCustomFieldError && (
                        <div className='text-red-500 text-sm mt-1'>
                          {formik.errors.customFieldNames?.[header]}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

ColumnMapping.displayName = 'ColumnMapping';

export default ColumnMapping;